import { Button, Grid, GridColumn, IFormHelperProps, PassField, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import * as React from 'react';
import LoginButton from '../components/LoginButton';
import { EMAIL_FORGOT_PASSWORD_PATH, EMAIL_REGISTER_PATH } from '../Routes';
import GaDataStore from '../stores/GaDataStore';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import { EMAIL_FIELD_ID, PW_FIELD_ID, emailFieldHelper, getHelperErrorMessage, helperInErrorState, notEmptyFieldHelper } from '../util/FieldHelperUtil';
import { isEmailValid } from '../util/ValidationUtil';
import useStores from '../stores/UseStores';
import RememberMe from './RememberMe';

const EmailLogin = observer((): React.JSX.Element => {
  const {
    emailLoginStore,
    routeStore: { route },
    uiStore: { getLoginPanelGridWidth, hideNotices, showErrorNoticeWithErroredFields, setSelectedTab },
    translateStore: { translate },
  } = useStores();
  const { emailLoginCredentials, submitting } = emailLoginStore;

  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const [emailHelper, setEmailHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [passwordHelper, setPasswordHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  const getEmailHelper = (forceIsDirty?: boolean) => emailFieldHelper(translate, emailLoginCredentials.emailAddress, forceIsDirty ?? hasSubmitted);
  const getPasswordHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, emailLoginCredentials.password, forceIsDirty ?? hasSubmitted, PW_FIELD_ID);

  React.useEffect(() => {
    hideNotices();
  }, []);

  React.useEffect(() => {
    setEmailHelper(getEmailHelper());
  }, [emailLoginCredentials.emailAddress, hasSubmitted]);

  React.useEffect(() => {
    setPasswordHelper(getPasswordHelper());
  }, [emailLoginCredentials.password, hasSubmitted]);

  const isInvalid = () => {
    return !emailLoginCredentials.emailAddress || !emailLoginCredentials.password || !isEmailValid(emailLoginCredentials.emailAddress);
  };

  const handleFormSubmit = (event: React.FormEvent): void => {
    runInTeliaRumService('Autentimine (email)', () => {
      event.preventDefault();
      setHasSubmitted(true);
      if (!isInvalid()) {
        emailLoginStore.handleLogin();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getEmailHelper(true), getPasswordHelper(true)]);
      }
    });
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, url: string, buttonText: string) => {
    event.preventDefault();
    route(url + window.location.search);
    GaDataStore.emailAdditionalButton(buttonText);
  };

  return (
    <Text>
      <form onSubmit={handleFormSubmit} id="emailLogin">
        <Grid align={['center-sm']}>
          <GridColumn width={getLoginPanelGridWidth()}>
            <Text>
              <p>{translate('panel.email.title')}</p>
              <TextField
                label={translate('panel.email.input')}
                id={EMAIL_FIELD_ID}
                value={emailLoginCredentials.emailAddress}
                onChange={(value) => {
                  emailLoginCredentials.emailAddress = value;
                }}
                isInvalid={helperInErrorState(emailHelper)}
                errorMessage={getHelperErrorMessage(emailHelper)}
                required={true}
                input={{ autoComplete: 'email' }}
              />
              <PassField
                label={translate('panel.email.password')}
                id={PW_FIELD_ID}
                value={emailLoginCredentials.password}
                onChange={(value) => {
                  emailLoginCredentials.password = value;
                }}
                isInvalid={helperInErrorState(passwordHelper)}
                errorMessage={getHelperErrorMessage(passwordHelper)}
                required={true}
                input={{ autoComplete: 'current-password' }}
              />
              <RememberMe tabItemId="emailLoginTab" />
              <LoginButton isLoading={submitting} disabled={submitting} onClick={() => setSelectedTab('email')} />
              <div className="text-small">
                <p>
                  <Button
                    url={EMAIL_FORGOT_PASSWORD_PATH + window.location.search}
                    link={true}
                    size="small"
                    type="text-color"
                    text={translate('panel.email.forgotPassword')}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) =>
                      handleButtonClick(event, EMAIL_FORGOT_PASSWORD_PATH, 'Unustasid parooli?')
                    }
                  />
                </p>
                <p>
                  <Button
                    url={EMAIL_REGISTER_PATH + window.location.search}
                    link={true}
                    size="small"
                    type="text-color"
                    text={translate('panel.email.createAccount')}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) =>
                      handleButtonClick(event, EMAIL_REGISTER_PATH, 'Loo kasutajakonto')
                    }
                  />
                </p>
              </div>
            </Text>
          </GridColumn>
        </Grid>
      </form>
    </Text>
  );
});

export default EmailLogin;
